import type { ReconciliationCommentary } from '@aloft/toolkit-shared';

export const BASEMENT_AREA_DISCLOSURE_HEADING = 'Basement Area Disclosure:';

const DISCLOSURE_BLOCK_REGEX = new RegExp(
  String.raw`\n*${BASEMENT_AREA_DISCLOSURE_HEADING}[\s\S]*$`,
);

export const extractBasementAreaDisclosureNote = (commentary?: string): string => {
  const match = commentary?.match(DISCLOSURE_BLOCK_REGEX);
  if (!match) return '';
  return match[0].replace(BASEMENT_AREA_DISCLOSURE_HEADING, '').trim();
};

export const hasBasementAreaDisclosureNote = (commentary?: string): boolean =>
  extractBasementAreaDisclosureNote(commentary).length > 0;

export const mergeBasementAreaDisclosureNote = (
  commentary: string | undefined,
  note: string,
): string => {
  const base = (commentary ?? '').replace(DISCLOSURE_BLOCK_REGEX, '').trimEnd();
  const trimmedNote = note.trim();
  if (!trimmedNote) return base;
  return base
    ? `${base}\n\n${BASEMENT_AREA_DISCLOSURE_HEADING} ${trimmedNote}`
    : `${BASEMENT_AREA_DISCLOSURE_HEADING} ${trimmedNote}`;
};

export const resolveBasementAreaDisclosureNote = (
  reconciliation?: Pick<
    ReconciliationCommentary,
    'basementAreaDisclosure' | 'reconciliationCommentary' | 'aivReconciliationCommentary'
  >,
  hasArvOption = false,
): string => {
  const stored = reconciliation?.basementAreaDisclosure?.trim();
  if (stored) return stored;
  const commentary = hasArvOption
    ? (reconciliation?.aivReconciliationCommentary ?? reconciliation?.reconciliationCommentary)
    : reconciliation?.reconciliationCommentary;
  return extractBasementAreaDisclosureNote(commentary);
};

export const applyBasementAreaDisclosureToReconciliation = (
  reconciliation: ReconciliationCommentary | undefined,
  note: string,
  hasArvOption: boolean,
): Pick<
  ReconciliationCommentary,
  | 'basementAreaDisclosure'
  | 'reconciliationCommentary'
  | 'aivReconciliationCommentary'
  | 'arvReconciliationCommentary'
> => {
  const trimmedNote = note.trim();
  if (hasArvOption) {
    const mergedAiv = mergeBasementAreaDisclosureNote(
      reconciliation?.aivReconciliationCommentary ?? reconciliation?.reconciliationCommentary,
      trimmedNote,
    );
    return {
      basementAreaDisclosure: trimmedNote,
      aivReconciliationCommentary: mergedAiv,
      arvReconciliationCommentary: mergeBasementAreaDisclosureNote(
        reconciliation?.arvReconciliationCommentary,
        trimmedNote,
      ),
      reconciliationCommentary: mergedAiv,
    };
  }
  return {
    basementAreaDisclosure: trimmedNote,
    reconciliationCommentary: mergeBasementAreaDisclosureNote(
      reconciliation?.reconciliationCommentary,
      trimmedNote,
    ),
  };
};
